import { useState } from "react";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { useQuery } from "react-query";
import { getProductsByCategory } from "../../../../../../ecommerce/services/productService";
import CustomButton from "../../../../../../components/Button/CustomButton";
import { QUERY_KEYS } from "../../../../../../utils/react-query/queryKeys";
import {
  useGetComplementaries,
  useAddComplementary,
  useUpdateComplementary,
  useDeleteComplementary,
} from "../../../../../../utils/react-query/querySupplier/queries";
import { ComplementaryItem } from "../../../../../../privatePages/Inventory/components/ProductsTable/types";

interface Props {
  productId: number;
}

interface CategoryProduct {
  product_id: number;
  name: string;
}

type Unit = "m2" | "unidad";

interface EditState {
  id: number;
  quantity: string;
  unit: Unit;
}

const UNIT_OPTIONS: { value: Unit; label: string }[] = [
  { value: "m2", label: "m²" },
  { value: "unidad", label: "unidad(es)" },
];

/**
 * Formats a complementary's quantity + unit for display in read mode.
 * "m2" → "10 m²" · "unidad" → "3 unidades" (singular handled gracefully)
 */
function formatComplementary(item: ComplementaryItem): string {
  const unit = UNIT_OPTIONS.find((u) => u.value === item.unit);
  if (item.unit === "m2") {
    return `${item.quantity} m²`;
  }
  if (item.unit === "unidad") {
    return `${item.quantity} ${item.quantity === 1 ? "unidad" : "unidades"}`;
  }
  return `${item.quantity} ${unit?.label ?? item.unit}`;
}

const ComplementaryForm = ({ productId }: Props) => {
  const [selectedProduct, setSelectedProduct] =
    useState<CategoryProduct | null>(null);
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState<Unit>("m2");
  const [editState, setEditState] = useState<EditState | null>(null);

  const { data: complementaries = [], isLoading: isLoadingList } =
    useGetComplementaries(productId);

  const { data: categoryProducts = [], isLoading: isLoadingProducts } =
    useQuery(
      [QUERY_KEYS.GET_SUPPLIER_PRODUCTS_CATEGORY, 10],
      () => getProductsByCategory(10, true),
      { refetchOnWindowFocus: false }
    );

  const { mutateAsync: addComplementary, isLoading: isAdding } =
    useAddComplementary(productId);
  const { mutateAsync: updateComplementary, isLoading: isUpdating } =
    useUpdateComplementary(productId);
  const { mutateAsync: deleteComplementary } = useDeleteComplementary(productId);

  const handleAdd = async () => {
    if (!selectedProduct) return;
    await addComplementary({
      complementary_product_id: selectedProduct.product_id,
      quantity: Number(quantity),
      unit,
    });
    setSelectedProduct(null);
    setQuantity("");
    setUnit("m2");
  };

  const handleEditStart = (item: ComplementaryItem) => {
    setEditState({
      id: item.id,
      quantity: String(item.quantity),
      unit: item.unit,
    });
  };

  const handleEditSave = async () => {
    if (!editState) return;
    await updateComplementary({
      id: editState.id,
      quantity: Number(editState.quantity),
      unit: editState.unit,
    });
    setEditState(null);
  };

  const handleDelete = async (id: number) => {
    await deleteComplementary(id);
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 0" }}>
      <Typography variant="h6" gutterBottom>
        Productos Complementarios
      </Typography>

      {/* Existing complementaries list */}
      {isLoadingList ? (
        <CircularProgress size={24} />
      ) : (
        <div style={{ marginBottom: 24 }}>
          {complementaries.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No hay complementarios asignados.
            </Typography>
          )}
          {complementaries.map((item: ComplementaryItem) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ flex: 1 }}>
                <Typography variant="body2">
                  <strong>{item.name}</strong>
                </Typography>
                {editState?.id === item.id ? (
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <TextField
                      label="Cantidad"
                      size="small"
                      type="number"
                      value={editState.quantity}
                      onChange={(e) =>
                        setEditState((s) =>
                          s ? { ...s, quantity: e.target.value } : s
                        )
                      }
                      style={{ width: 120 }}
                    />
                    <TextField
                      select
                      label="Unidad"
                      size="small"
                      value={editState.unit}
                      onChange={(e) =>
                        setEditState((s) =>
                          s ? { ...s, unit: e.target.value as Unit } : s
                        )
                      }
                      style={{ width: 140 }}
                    >
                      {UNIT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    {formatComplementary(item)}
                  </Typography>
                )}
              </div>
              {editState?.id === item.id ? (
                <>
                  <IconButton
                    size="small"
                    onClick={handleEditSave}
                    disabled={isUpdating}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditState(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleEditStart(item)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new complementary */}
      <Typography variant="subtitle1" gutterBottom>
        Agregar complementario
      </Typography>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Autocomplete
          options={categoryProducts}
          getOptionLabel={(option: CategoryProduct) => option.name}
          loading={isLoadingProducts}
          value={selectedProduct}
          onChange={(_e, value) => setSelectedProduct(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Producto complementario"
              variant="outlined"
              size="small"
            />
          )}
        />
        <div style={{ display: "flex", gap: 12 }}>
          <TextField
            label="Cantidad"
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            style={{ flex: 1 }}
          />
          <TextField
            select
            label="Unidad"
            size="small"
            value={unit}
            onChange={(e) => setUnit(e.target.value as Unit)}
            style={{ flex: 1 }}
          >
            {UNIT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div style={{ alignSelf: "flex-start" }}>
          <CustomButton
            color="primary"
            onClick={handleAdd}
            disabled={!selectedProduct || isAdding}
            isLoading={isAdding}
          >
            Agregar
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default ComplementaryForm;
